import { useState, useMemo, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import axios from 'axios';
import { Star, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from './constants';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
// Lazy-load Stripe.js only once. If the publishable key is the placeholder
// or missing, we surface a friendly fallback instead of trying to mount.
const stripePromise = PUBLISHABLE_KEY && !PUBLISHABLE_KEY.includes('PLACEHOLDER')
  ? loadStripe(PUBLISHABLE_KEY)
  : null;

/**
 * Embedded Stripe Checkout — renders a compact 3-card plan picker, then
 * mounts <EmbeddedCheckout> inline once the user picks a plan. Keeps the
 * coach inside our app the whole time.
 *
 * Falls back to the existing hosted-redirect flow if Stripe.js fails to
 * load (e.g. ad blocker) or the publishable key is missing.
 */
export default function EmbeddedPaymentBlock({ darkBg }) {
  const [plans, setPlans] = useState(null);
  const [pricingCfg, setPricingCfg] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(!stripePromise);
  const sessionCacheRef = useRef({}); // { [plan_id]: client_secret }

  useEffect(() => {
    axios.get(`${API}/stripe/plans`)
      .then(r => setPlans(r.data.plans))
      .catch(() => toast.error('Could not load pricing'));
    axios.get(`${API}/pricing`)
      .then(r => setPricingCfg(r.data))
      .catch(() => {});
  }, []);

  // When user picks a plan, fetch (or reuse) a client_secret and mount the form.
  const pickPlan = async (planId) => {
    if (selectedPlan === planId && clientSecret) return; // no-op
    setSelectedPlan(planId);

    if (sessionCacheRef.current[planId]) {
      setClientSecret(sessionCacheRef.current[planId]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/stripe/create-embedded-session`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      sessionCacheRef.current[planId] = data.client_secret;
      setClientSecret(data.client_secret);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start payment';
      toast.error(msg);
      setSelectedPlan(null);
    } finally {
      setLoading(false);
    }
  };

  // Fallback to hosted redirect flow.
  const fallbackToHosted = async (planId) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/stripe/create-checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
      setLoading(false);
    }
  };

  const options = useMemo(() => clientSecret ? { clientSecret } : null, [clientSecret]);

  const cardCls = darkBg
    ? 'border-white/15 bg-white/5 hover:border-white/40'
    : 'border-gray-200 bg-white hover:border-gray-400';
  const selectedCls = darkBg
    ? 'border-emerald-400 bg-emerald-500/10'
    : 'border-emerald-500 bg-emerald-50';

  return (
    <div className="mt-6 space-y-4" data-testid="embedded-payment-block">
      {/* Compact plan picker */}
      <div className="grid grid-cols-1 gap-2">
        {(() => {
          const fmt = (n) => `\u20AC${(Number(n) || 0).toFixed(2)}`;
          const m = pricingCfg?.plans?.monthly;
          const a = pricingCfg?.plans?.annual;
          const l = pricingCfg?.plans?.lifetime;
          const items = [
            {
              id: 'monthly',
              label: m?.label || 'Monthly',
              price: `${fmt(m?.effective_amount ?? m?.base_amount ?? 8.99)}/mo`,
              sub: `${fmt(m?.annual_equivalent ?? 107.88)}/year`,
              icon: null,
              discount: m?.is_discounted ? (m.discount_label || `Save ${Math.round(m.discount_percent||0)}%`) : '',
              intro: '',
            },
            {
              id: 'annual',
              label: `${a?.label || 'Annual'} (recommended)`,
              price: (() => {
                if (!a) return `${fmt(1.99)} trial`;
                const tAmt = a.trial_amount;
                const hasFree = tAmt !== null && tAmt !== undefined && Number(tAmt) === 0;
                const hasPaid = tAmt !== null && tAmt !== undefined && Number(tAmt) > 0 && a.trial_enabled !== false;
                if (hasFree && a.trial_enabled !== false) return `${fmt(0)} today`;
                if (hasPaid) return `${fmt(tAmt)} trial`;
                return `${fmt(a.effective_amount ?? a.base_amount ?? 83.88)}/year`;
              })(),
              sub: `Then ${fmt(a?.base_amount ?? 83.88)}/year`,
              icon: <Star className="w-4 h-4 text-emerald-600" />,
              discount: a?.is_discounted ? (a.discount_label || `Save ${Math.round(a.discount_percent||0)}%`) : '',
              intro: (() => {
                if (!a || a.trial_enabled === false) return '';
                const tDays = Number(a.trial_days) || 0;
                if (tDays <= 0) return '';
                const tAmt = a.trial_amount;
                if (tAmt !== null && tAmt !== undefined && Number(tAmt) > 0) {
                  const unit = tDays === 7 ? '1 week' : (tDays % 7 === 0 ? `${tDays / 7} weeks` : `${tDays} days`);
                  return `Try ${unit} for \u20AC${Number(tAmt).toFixed(2)}`;
                }
                if (tAmt !== null && tAmt !== undefined && Number(tAmt) === 0) {
                  return `Free ${tDays}-day trial`;
                }
                return '';
              })(),
            },
            {
              id: 'lifetime',
              label: l?.label || 'Lifetime Deal',
              price: fmt(l?.effective_amount ?? l?.base_amount ?? 59.99),
              sub: 'One-time payment',
              icon: <Crown className="w-4 h-4 text-amber-600" />,
              discount: l?.is_discounted ? (l.discount_label || `Save ${Math.round(l.discount_percent||0)}%`) : '',
              intro: '',
            },
          ];
          return items.map(p => {
          const isSelected = selectedPlan === p.id;
          const ltdSold = p.id === 'lifetime' && plans?.lifetime?.ltd_remaining === 0;
          return (
            <button
              key={p.id}
              type="button"
              disabled={loading || ltdSold}
              onClick={() => fallbackMode ? fallbackToHosted(p.id) : pickPlan(p.id)}
              className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                isSelected ? selectedCls : cardCls
              } ${ltdSold ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid={`embedded-plan-${p.id}`}
            >
              <div>
                <div className={`font-bold flex items-center gap-1.5 ${darkBg ? 'text-white' : 'text-gray-900'}`}>
                  {p.icon} {p.label}
                </div>
                <div className={`text-xs ${darkBg ? 'text-white/60' : 'text-gray-500'}`}>{p.sub}</div>
                {p.intro && (
                  <div
                    className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      darkBg
                        ? 'bg-emerald-400/15 border border-emerald-400/30 text-emerald-300'
                        : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-700'
                    }`}
                    data-testid={`embedded-plan-${p.id}-intro-pill`}
                  >
                    {p.intro}
                  </div>
                )}
                {p.discount && (
                  <div className="text-[10px] text-amber-600 font-bold uppercase tracking-wider mt-0.5" data-testid={`embedded-plan-${p.id}-discount`}>
                    {p.discount}
                  </div>
                )}
              </div>
              <div className={`font-black ${darkBg ? 'text-white' : 'text-gray-900'}`}>{p.price}</div>
            </button>
          );
          });
        })()}
      </div>

      {/* Currency note */}
      <p className={`text-center text-[11px] ${darkBg ? 'text-white/50' : 'text-gray-500'}`} data-testid="embedded-currency-note">
        Charged in EUR. See Terms.
      </p>

      {/* Loading state */}
      {loading && !clientSecret && (
        <div className="flex justify-center py-6 text-gray-500" data-testid="embedded-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="ml-2 text-sm">Preparing secure payment…</span>
        </div>
      )}

      {/* Embedded Stripe checkout — wrapped in a brand-aligned container.
          Inner Stripe iframe theming itself is controlled in the Stripe
          Dashboard (Settings → Branding). The chrome around it picks up our
          emerald accent so it reads as part of the Grassroots flow. */}
      {!fallbackMode && clientSecret && options && (
        <div
          className={`rounded-xl overflow-hidden border-2 ${darkBg ? 'border-emerald-400/40 bg-white' : 'border-emerald-500/40 bg-white shadow-sm'}`}
          data-testid="embedded-checkout-container"
        >
          <div className={`px-4 py-2 text-xs font-semibold tracking-wide uppercase ${darkBg ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
            Secure payment by Stripe
          </div>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}

      {/* Fallback mode — placeholder publishable key or Stripe.js failed to load */}
      {fallbackMode && (
        <div
          className={`text-xs text-center px-4 py-3 rounded-lg ${darkBg ? 'bg-white/10 text-white/80' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
          data-testid="embedded-fallback-notice"
        >
          Embedded checkout requires a Stripe publishable key. Pick a plan above to continue with Stripe&apos;s secure hosted checkout instead.
        </div>
      )}
    </div>
  );
}
